import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Camera, PhoneOutgoing, Play, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useApi, errMsg, fmtTime } from "@/lib/api";
import { PageHead, card, Field, Sel, inputCls, Empty } from "@/components/admin/kit";
import { SampleReport, useFetch } from "@/components/p4/shared";
import TestCall from "./TestCall";

export function SayBtn({ api, text, testid }) {
  const play = async () => {
    if (!text?.trim()) return toast.error("Type the name first");
    try { const r = await api.post("/admin/demo/say", { text }, { responseType: "blob" }); new Audio(URL.createObjectURL(r.data)).play(); } catch (e) { toast.error(errMsg(e)); }
  };
  return <Button type="button" size="sm" variant="outline" onClick={play} data-testid={testid}><Play size={14} /> Play</Button>;
}

function usePoll(load, ms = 4000) {
  useEffect(() => { const t = setInterval(load, ms); return () => clearInterval(t); }, [load, ms]);
}

function DemoCall({ api, info }) {
  const [to, setTo] = useState("");
  const [sample, setSample] = useState("");
  const [busy, setBusy] = useState(false);
  const [list, reload] = useFetch(api, "/admin/demo/calls");
  usePoll(reload);
  const nums = info?.test_numbers || [];
  const go = async () => {
    setBusy(true);
    try { await api.post("/admin/demo/call", { to: to || nums[0]?.value, sample }); toast.success("Demo call booked — your phone rings in about 1 minute"); reload(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className={`${card} grid gap-3`} data-testid="demo-call-card">
      <p className="flex items-center gap-2 font-bold"><PhoneOutgoing size={16} /> Demo call</p>
      <div className="grid gap-3 sm:grid-cols-3">
        <Field label="Phone"><Sel value={to || nums[0]?.value || ""} onChange={setTo} options={nums.map((n) => [n.value, n.label])} placeholder="Verified number" testid="demo-call-phone" /></Field>
        <Field label="Sample"><Sel value={sample} onChange={setSample} options={(info?.samples || []).map((s) => [s.key, `${s.no} · ${s.title}${s.ready ? "" : " (no audio)"}`])} placeholder="Pick 01–12" testid="demo-call-sample" /></Field>
        <div className="flex items-end"><Button className="w-full" disabled={busy || !sample || !nums.length} onClick={go} data-testid="demo-call-btn">Call in 1 minute</Button></div>
      </div>
      <p className="text-xs text-muted-foreground">Test mode: only your verified numbers. Counts toward the 5 test calls a day. Logged as “demo”.</p>
      {(list || []).slice(0, 5).map((d) => (
        <p key={d.id} className="border-t pt-2 text-sm" data-testid={`demo-call-row-${d.id}`}><b>{d.patient_name}</b> · {d.to_masked} · {fmtTime(d.at)} · <span className="font-semibold">{d.result || d.status}</span>{d.error || d.call_error ? <span className="text-red-600"> · {d.error || d.call_error}</span> : null}</p>
      ))}
    </div>
  );
}

const L0 = { clinic: "", clinic_voice: "", doctor: "", doctor_voice: "", patient: "", phone: "" };

function LiveForm({ api, nums, onDone }) {
  const [f, setF] = useState(L0);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    const fd = new FormData();
    Object.entries({ ...f, phone: f.phone || nums[0]?.value || "" }).forEach(([k, v]) => fd.append(k, v));
    photos.forEach((p) => fd.append("photos", p));
    setBusy(true);
    try { const r = await api.post("/admin/demo/live", fd); toast.success(`Case ${r.data.case_no} created — AI is reading it`); setF(L0); setPhotos([]); onDone(r.data); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="grid gap-3 sm:grid-cols-2" data-testid="live-demo-form">
      <Field label="Clinic name *"><input required className={inputCls} value={f.clinic} onChange={set("clinic")} data-testid="live-clinic" /></Field>
      <Field label="How to say the clinic" hint="Telugu letters work best"><div className="flex gap-2"><input className={inputCls} value={f.clinic_voice} onChange={set("clinic_voice")} placeholder={f.clinic} data-testid="live-clinic-voice" /><SayBtn api={api} text={f.clinic_voice || f.clinic} testid="live-clinic-play" /></div></Field>
      <Field label="Doctor's name *"><input required className={inputCls} value={f.doctor} onChange={set("doctor")} data-testid="live-doctor" /></Field>
      <Field label="How to say the doctor"><div className="flex gap-2"><input className={inputCls} value={f.doctor_voice} onChange={set("doctor_voice")} placeholder={f.doctor} data-testid="live-doctor-voice" /><SayBtn api={api} text={f.doctor_voice || f.doctor} testid="live-doctor-play" /></div></Field>
      <Field label="Patient name in the call" hint="Leave empty to use the doctor's name"><input className={inputCls} value={f.patient} onChange={set("patient")} data-testid="live-patient" /></Field>
      <Field label="Call goes to"><Sel value={f.phone || nums[0]?.value || ""} onChange={(v) => setF({ ...f, phone: v })} options={nums.map((n) => [n.value, n.label])} placeholder="Verified number" testid="live-phone" /></Field>
      <label className="flex cursor-pointer items-center gap-2 rounded-xl border-2 border-dashed border-[var(--violet)] p-3 text-sm font-semibold text-[var(--violet)] sm:col-span-2" data-testid="live-photo-label">
        <Camera size={18} /> {photos.length ? `${photos.length} photo(s) ready` : "Take photo of the prescription"}
        <input type="file" accept="image/*" capture="environment" multiple className="hidden" onChange={(e) => setPhotos([...e.target.files].slice(0, 3))} data-testid="live-photo-input" />
      </label>
      <Button disabled={busy || !photos.length} className="sm:col-span-2" data-testid="live-submit">{busy ? "Uploading…" : "Read prescription"}</Button>
    </form>
  );
}

function LiveRow({ api, d, reload }) {
  const [script, setScript] = useState("");
  const [busy, setBusy] = useState(false);
  const act = async (fn, msg) => { setBusy(true); try { const r = await fn(); if (msg) toast.success(msg); reload(); return r?.data; } catch (e) { toast.error(errMsg(e)); return null; } finally { setBusy(false); } };
  const approved = d.status === "approved";
  return (
    <div className="grid gap-2 border-t py-3 text-sm" data-testid={`live-row-${d.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="rounded-full bg-red-600 px-2 text-xs font-bold text-white">DEMO</span>
        <b>{d.clinic}</b><span className="text-muted-foreground">· {d.doctor} · {d.case_no} · {d.to_masked} · deletes {fmtTime(d.expires_at)}</span>
        <span className="font-semibold" data-testid={`live-status-${d.id}`}>{approved ? "approved" : d.ai_status === "done" ? "ready to check" : d.ai_status === "failed" ? "AI could not read" : "AI reading…"}</span>
        <Button size="sm" variant="ghost" className="ml-auto" onClick={() => window.confirm("Delete this demo now?") && act(() => api.delete(`/admin/demo/live/${d.id}`), "Deleted")} data-testid={`live-delete-${d.id}`}><Trash2 size={14} /></Button>
      </div>
      {!approved && <Link to={`/admin/submissions/case/${d.submission_id}`} className="font-semibold text-[var(--violet)] underline" data-testid={`live-open-case-${d.id}`}>Open case to check and approve →</Link>}
      {approved && (
        <div className="flex flex-wrap gap-2">
          {d.slots.map((s) => <Button key={s} size="sm" disabled={busy} onClick={() => act(() => api.post(`/admin/demo/live/${d.id}/call`, { slot: s }), "Calling now")} data-testid={`live-call-${s}-${d.id}`}><PhoneOutgoing size={14} /> Call now · {s}</Button>)}
          {d.slots.map((s) => <Button key={`t${s}`} size="sm" variant="outline" onClick={async () => { const r = await act(() => api.get(`/admin/demo/live/${d.id}/script?slot=${s}`)); if (r) setScript(r.text); }} data-testid={`live-script-${s}-${d.id}`}>Script · {s}</Button>)}
        </div>
      )}
      {script && <p className="rounded-xl bg-[var(--lav)] p-3" data-testid={`live-script-text-${d.id}`}>{script}</p>}
      {d.calls.map((c, i) => <p key={i} className="text-xs">{fmtTime(c.created_at)} · {c.slot} · <b>{c.result}</b>{c.error ? <span className="text-red-600"> · {c.error}</span> : null}</p>)}
    </div>
  );
}

function LiveDemo({ api, info }) {
  const [list, reload] = useFetch(api, "/admin/demo/live");
  usePoll(reload, 5000);
  return (
    <div className={`${card} grid gap-3`} data-testid="live-demo-card">
      <p className="font-bold">Live demo — the doctor's own prescription</p>
      <p className="text-xs text-muted-foreground">1. Type the clinic and doctor (tap Play to check the names) · 2. Photo · 3. Check and approve the case · 4. Call now. Marked DEMO, admin-only, never in reports or billing, deleted automatically after 24 hours.</p>
      <LiveForm api={api} nums={info?.test_numbers || []} onDone={reload} />
      {!(list || []).length ? <Empty>No live demos in the last 24 hours.</Empty> : list.map((d) => <LiveRow key={d.id} api={api} d={d} reload={reload} />)}
    </div>
  );
}

export default function DemoTools() {
  const api = useApi();
  const [info] = useFetch(api, "/admin/demo/samples");
  const [sample, setSample] = useState(false);
  return (
    <div className="grid gap-4" data-testid="demo-tools-page">
      <PageHead title="Demo tools" sub="For meeting doctors. All demo calls go only to your verified test number.">
        <Button variant={sample ? "default" : "outline"} onClick={() => setSample(!sample)} data-testid="demo-sample-report-btn">{sample ? "Hide sample report" : "Sample report"}</Button>
      </PageHead>
      {sample && <SampleReport api={api} />}
      {info && info.provider === "simulator" && <p className="rounded-xl bg-amber-100 p-3 text-sm font-semibold text-amber-800" data-testid="demo-provider-warning">Call provider is Simulator — choose Twilio in Settings for real demo calls.</p>}
      <DemoCall api={api} info={info} />
      <LiveDemo api={api} info={info} />
      <div className="mt-4 border-t-2 border-dashed border-[var(--lav-3)] pt-4" data-testid="demo-real-test-call"><TestCall /></div>
    </div>
  );
}
